// A light, reversible shift -- not real cryptography, just enough that the address never sits
// in the server-rendered HTML as a plain, regex-matchable "x@y.z" string (what most bulk email
// harvesters actually scan for) or inside a literal "mailto:" href. Decoded back client-side,
// almost immediately after mount, so a real visitor with JavaScript on (effectively everyone)
// never notices a delay -- this is about raising the bar against automated scraping of the
// static page source, the same practical goal as Cloudflare's email obfuscation, not
// unbreakable encryption against a determined, JS-executing scraper.
const SHIFT = 5;

export function encodeEmail(email: string): string {
  return email
    .split("")
    .map((ch) => String.fromCharCode(ch.charCodeAt(0) + SHIFT))
    .join("");
}

export function decodeEmail(encoded: string): string {
  return encoded
    .split("")
    .map((ch) => String.fromCharCode(ch.charCodeAt(0) - SHIFT))
    .join("");
}

/** What a visitor without JavaScript (or a scraper that doesn't execute it) sees instead of the
 * real address -- still genuinely readable by a person, just not a pattern an email-regex
 * matches. */
export function spelledOutPlaceholder(email: string): string {
  const [user, domain] = email.split("@");
  return `${user} (at) ${domain.replace(".", " (dot) ")}`;
}

const MAILTO_LINK_PATTERN = /<a\s+([^>]*?)href="mailto:([^"?]+)(\?[^"]*)?"([^>]*)>([\s\S]*?)<\/a>/gi;

/**
 * Pure string transform (no DOM/browser API), so it's safe to call from a server component --
 * that matters here specifically: a hardcoded fallback string passed as a prop from a server
 * component into a client component gets serialized into the page's hydration payload exactly
 * as given, regardless of what that client component's own render logic later does with it. If
 * the raw "mailto:real@address" text is still in that prop value, it ends up in the page's
 * initial HTTP response either way, uncloaked -- cloaking has to happen before the value ever
 * crosses that boundary, not after.
 */
export function cloakMailtoLinks(html: string): string {
  return html.replace(MAILTO_LINK_PATTERN, (_match, before, email) => {
    void before; // Any other attributes (class, target, ...) on the original link are dropped --
    // deliberately: this always re-renders as a plain restored <a>, and nothing in this app puts
    // more than an href/text on a mailto link.
    return `<span data-cloaked-mailto="${encodeEmail(email)}">${spelledOutPlaceholder(email)}</span>`;
  });
}
